import React, { useState, useEffect } from 'react';
import { useTabParam } from '../../hooks/useTabParam';
import { useNavigate } from 'react-router-dom';
import { Calendar, CheckCircle, XCircle, Clock, Plus, ArrowLeft, User, Briefcase, FileText, Trash2, Settings, Edit, Calendar as CalendarIcon, Download, ChevronDown } from 'lucide-react';
import api from '../../services/api';
import { leaveTypeLabel } from '../../utils/leaveTypes';
import HolidayCalendar from '../../components/HolidayCalendar';
import AbsencesTab from '../../components/leaves/AbsencesTab';
import { useDialogs } from '../../components/Dialogs';

// "½ day" / "1 day" / "3 days"
const formatLeaveDays = (leave) =>
    leave.isHalfDay || leave.daysCount === 0.5 ? '½ day' : `${leave.daysCount} ${leave.daysCount === 1 ? 'day' : 'days'}`;

// A half day is only allowed on a single date, so while it's ticked the end
// date follows the start date, and picking a different end date clears it.
const setDates = (formData, patch) => {
    const next = { ...formData, ...patch };
    if (next.isHalfDay && patch.startDate !== undefined) next.endDate = patch.startDate;
    if (next.isHalfDay && next.startDate && next.endDate && next.startDate !== next.endDate) next.isHalfDay = false;
    return next;
};

const HalfDayToggle = ({ formData, setFormData, id }) => (
    <label htmlFor={id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem', cursor: 'pointer' }}>
        <input
            id={id}
            type="checkbox"
            checked={!!formData.isHalfDay}
            onChange={e => setFormData({
                ...formData,
                isHalfDay: e.target.checked,
                endDate: e.target.checked && formData.startDate ? formData.startDate : formData.endDate,
            })}
        />
        Half day (counts as 0.5 day, single date only)
    </label>
);

const ApplyLeaveModal = ({ show, onClose, onSuccess, balance, holidays = [] }) => {
    const [formData, setFormData] = useState({
        type: 'CASUAL',
        startDate: '',
        endDate: '',
        isHalfDay: false,
        reason: ''
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    if (!show) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        try {
            await api.post('/leaves/apply', formData);
            onSuccess();
            onClose();
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to apply');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
            <div style={{ background: 'var(--surface)', padding: '2rem', borderRadius: '1rem', width: '100%', maxWidth: '500px' }}>
                <h2 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '1rem' }}>Apply for Leave</h2>
                {error && <div style={{ color: 'red', marginBottom: '1rem' }}>{error}</div>}
                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    <div>
                        <label htmlFor="leave-type" style={{ display: 'block', marginBottom: '0.5rem' }}>Leave Type</label>
                        <select
                            id="leave-type"
                            value={formData.type}
                            onChange={e => setFormData({ ...formData, type: e.target.value })}
                            style={{ width: '100%', padding: '0.5rem', border: '1px solid var(--input-border)', borderRadius: '0.5rem', background: 'var(--input-bg)', color: 'var(--text-main)' }}
                        >
                            <option value="CASUAL">Casual Leave ({balance?.casual || 0} remaining)</option>
                            <option value="SICK">Sick Leave ({balance?.sick || 0} remaining)</option>
                            <option value="EARNED">Earned Leave ({balance?.earned || 0} remaining)</option>
                            <option value="UNPAID">Leave Without Pay (LWP)</option>
                        </select>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                        <div>
                            <label htmlFor="start-date" style={{ display: 'block', marginBottom: '0.5rem' }}>Start Date</label>
                            <input
                                id="start-date"
                                type="date"
                                required
                                value={formData.startDate}
                                onChange={e => setFormData(setDates(formData, { startDate: e.target.value }))}
                                style={{ width: '100%', padding: '0.5rem', border: '1px solid var(--input-border)', borderRadius: '0.5rem', background: 'var(--input-bg)', color: 'var(--text-main)' }}
                            />
                        </div>
                        <div>
                            <label htmlFor="end-date" style={{ display: 'block', marginBottom: '0.5rem' }}>End Date</label>
                            <input
                                id="end-date"
                                type="date"
                                required
                                value={formData.endDate}
                                onChange={e => setFormData(setDates(formData, { endDate: e.target.value }))}
                                style={{ width: '100%', padding: '0.5rem', border: '1px solid var(--input-border)', borderRadius: '0.5rem', background: 'var(--input-bg)', color: 'var(--text-main)' }}
                            />
                        </div>
                    </div>

                    <HalfDayToggle id="half-day" formData={formData} setFormData={setFormData} />

                    {/* Leave Duration & Effective Days Calculation */}
                    {formData.startDate && formData.endDate && (
                        (() => {
                            const start = new Date(formData.startDate);
                            const end = new Date(formData.endDate);

                            // Validate dates
                            if (isNaN(start.getTime()) || isNaN(end.getTime()) || start > end) {
                                return null;
                            }

                            let totalDays = 0;
                            let weekendCount = 0;
                            let holidayCount = 0;
                            const holidayDetails = [];

                            for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
                                totalDays++;
                                const dayOfWeek = d.getDay(); // 0 = Sun, 6 = Sat
                                const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

                                const holiday = holidays.find(h => {
                                    const hDate = new Date(h.date);
                                    return h.isActive && hDate.toDateString() === d.toDateString();
                                });

                                if (holiday) {
                                    holidayCount++;
                                    holidayDetails.push(holiday);
                                } else if (isWeekend) {
                                    weekendCount++;
                                }
                            }

                            const workingDays = totalDays - holidayCount - weekendCount;
                            const effectiveDays = formData.isHalfDay && workingDays > 0 ? 0.5 : workingDays;

                            return (
                                <div style={{ background: 'var(--bg-secondary)', padding: '1rem', borderRadius: '0.5rem', border: '1px solid var(--border)', marginBottom: '0.5rem' }}>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginBottom: '0.5rem' }}>
                                        <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>Total Duration:</div>
                                        <div style={{ fontWeight: '600', textAlign: 'right' }}>{totalDays} days</div>

                                        <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>Holidays:</div>
                                        <div style={{ fontWeight: '600', textAlign: 'right', color: '#d97706' }}>- {holidayCount}</div>

                                        <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>Weekends:</div>
                                        <div style={{ fontWeight: '600', textAlign: 'right', color: 'var(--text-muted)' }}>- {weekendCount}</div>

                                        <div style={{ borderTop: '1px solid #e5e7eb', gridColumn: '1 / -1', margin: '0.25rem 0' }}></div>

                                        <div style={{ fontWeight: '600', color: 'var(--text-main)' }}>Effective Leave:</div>
                                        <div style={{ fontWeight: 'bold', textAlign: 'right', color: '#2563eb' }}>{effectiveDays} days</div>
                                    </div>

                                    {holidayDetails.length > 0 && (
                                        <div style={{ marginTop: '0.5rem', padding: '0.5rem', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '0.25rem', fontSize: '0.85rem', color: '#92400e' }}>
                                            <strong>Included Holidays:</strong>
                                            <ul style={{ margin: '0.25rem 0 0 1.25rem', padding: 0 }}>
                                                {holidayDetails.map((h, i) => (
                                                    <li key={i}>{new Date(h.date).toLocaleDateString()}: {h.name}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            );
                        })()
                    )}

                    <div>
                        <label htmlFor="leave-reason" style={{ display: 'block', marginBottom: '0.5rem' }}>Reason</label>
                        <textarea
                            id="leave-reason"
                            required
                            rows="3"
                            value={formData.reason}
                            onChange={e => setFormData({ ...formData, reason: e.target.value })}
                            style={{ width: '100%', padding: '0.5rem', border: '1px solid var(--input-border)', borderRadius: '0.5rem', background: 'var(--input-bg)', color: 'var(--text-main)' }}
                        ></textarea>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                        <button type="button" onClick={onClose} className="btn btn-secondary">Cancel</button>
                        <button type="submit" disabled={loading} className="btn btn-primary">{loading ? 'Submitting...' : 'Apply'}</button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// Edit Leave Modal
const EditLeaveModal = ({ show, onClose, onSuccess, leave }) => {
    const [formData, setFormData] = useState({
        type: 'CASUAL',
        startDate: '',
        endDate: '',
        isHalfDay: false,
        reason: ''
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (leave) {
            setFormData({
                type: leave.type,
                startDate: new Date(leave.startDate).toISOString().split('T')[0],
                endDate: new Date(leave.endDate).toISOString().split('T')[0],
                isHalfDay: !!leave.isHalfDay,
                reason: leave.reason || ''
            });
        }
    }, [leave]);

    if (!show || !leave) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        try {
            await api.put(`/leaves/${leave.id}`, formData);
            onSuccess();
            onClose();
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to update');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
            <div style={{ background: 'var(--surface)', padding: '2rem', borderRadius: '1rem', width: '100%', maxWidth: '500px' }}>
                <h2 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '1rem' }}>Edit Leave Request</h2>
                {error && <div style={{ color: 'red', marginBottom: '1rem' }}>{error}</div>}
                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>

                    {/* Reuse similar form fields as ApplyModal - skipping full duplication for brevity in this replace block, but in real scenario would modularize form fields */}
                    <div>
                        <label htmlFor="edit-leave-type" style={{ display: 'block', marginBottom: '0.5rem' }}>Leave Type</label>
                        <select
                            id="edit-leave-type"
                            value={formData.type}
                            onChange={e => setFormData({ ...formData, type: e.target.value })}
                            style={{ width: '100%', padding: '0.5rem', border: '1px solid var(--input-border)', borderRadius: '0.5rem', background: 'var(--input-bg)', color: 'var(--text-main)' }}
                        >
                            <option value="CASUAL">Casual Leave</option>
                            <option value="SICK">Sick Leave</option>
                            <option value="EARNED">Earned Leave</option>
                            <option value="UNPAID">Leave Without Pay (LWP)</option>
                        </select>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                        <div>
                            <label htmlFor="edit-start-date" style={{ display: 'block', marginBottom: '0.5rem' }}>Start Date</label>
                            <input
                                id="edit-start-date"
                                type="date"
                                required
                                value={formData.startDate}
                                onChange={e => setFormData(setDates(formData, { startDate: e.target.value }))}
                                style={{ width: '100%', padding: '0.5rem', border: '1px solid var(--input-border)', borderRadius: '0.5rem', background: 'var(--input-bg)', color: 'var(--text-main)' }}
                            />
                        </div>
                        <div>
                            <label htmlFor="edit-end-date" style={{ display: 'block', marginBottom: '0.5rem' }}>End Date</label>
                            <input
                                id="edit-end-date"
                                type="date"
                                required
                                value={formData.endDate}
                                onChange={e => setFormData(setDates(formData, { endDate: e.target.value }))}
                                style={{ width: '100%', padding: '0.5rem', border: '1px solid var(--input-border)', borderRadius: '0.5rem', background: 'var(--input-bg)', color: 'var(--text-main)' }}
                            />
                        </div>
                    </div>

                    <HalfDayToggle id="edit-half-day" formData={formData} setFormData={setFormData} />

                    <div>
                        <label htmlFor="edit-leave-reason" style={{ display: 'block', marginBottom: '0.5rem' }}>Reason</label>
                        <textarea
                            id="edit-leave-reason"
                            required
                            rows="3"
                            value={formData.reason}
                            onChange={e => setFormData({ ...formData, reason: e.target.value })}
                            style={{ width: '100%', padding: '0.5rem', border: '1px solid var(--input-border)', borderRadius: '0.5rem', background: 'var(--input-bg)', color: 'var(--text-main)' }}
                        ></textarea>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                        <button type="button" onClick={onClose} className="btn btn-secondary">Cancel</button>
                        <button type="submit" disabled={loading} className="btn btn-primary">{loading ? 'Saving...' : 'Save Changes'}</button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// Balance Adjustment Modal
const AdjustBalanceModal = ({ show, onClose, users, onSuccess, selectedUser, setSelectedUser }) => {
    const [balance, setBalance] = useState({ sick: 0, casual: 0, earned: 0 });
    const [loading, setLoading] = useState(false);
    const [fetchLoading, setFetchLoading] = useState(false);
    const [msg, setMsg] = useState(null);

    // Fetch user balance when selected
    useEffect(() => {
        if (selectedUser) {
            setFetchLoading(true);
            api.get('/leaves/balance', { params: { userId: selectedUser } })
                .then(res => setBalance({ sick: res.data.sick, casual: res.data.casual, earned: res.data.earned }))
                .catch(() => setBalance({ sick: 0, casual: 0, earned: 0 }))
                .finally(() => setFetchLoading(false));
        }
    }, [selectedUser]);

    if (!show) return null;

    const handleSave = async () => {
        setLoading(true);
        try {
            await api.post('/leaves/balance/adjust', { userId: selectedUser, ...balance });
            setMsg('Balance updated!');
            if (onSuccess) onSuccess();
            setTimeout(() => setMsg(null), 2000);
        } catch (e) {
            alert('Failed to update');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
            <div style={{ background: 'var(--surface)', padding: '2rem', borderRadius: '1rem', width: '100%', maxWidth: '400px' }}>
                <h2 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '1rem' }}>Adjust Leave Balance</h2>
                {msg && <div style={{ color: 'green', marginBottom: '1rem' }}>{msg}</div>}
                <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem' }}>Select Employee</label>
                    <select
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid var(--input-border)', borderRadius: '0.5rem', background: 'var(--input-bg)', color: 'var(--text-main)' }}
                        value={selectedUser}
                        onChange={e => setSelectedUser(e.target.value)}
                    >
                        <option value="">-- Select --</option>
                        {users.map(u => <option key={u.id} value={u.id}>{u.name} ({u.email})</option>)}
                    </select>
                </div>

                {selectedUser && (
                    fetchLoading ? <div>Loading current balance...</div> : (
                        <div style={{ display: 'grid', gap: '1rem' }}>
                            <div>
                                <label>Sick Leave</label>
                                <input type="number" value={balance.sick} onChange={e => setBalance({ ...balance, sick: e.target.value })} style={{ width: '100%', padding: '0.5rem', border: '1px solid var(--input-border)', borderRadius: '0.5rem', background: 'var(--input-bg)', color: 'var(--text-main)' }} />
                            </div>
                            <div>
                                <label>Casual Leave</label>
                                <input type="number" value={balance.casual} onChange={e => setBalance({ ...balance, casual: e.target.value })} style={{ width: '100%', padding: '0.5rem', border: '1px solid var(--input-border)', borderRadius: '0.5rem', background: 'var(--input-bg)', color: 'var(--text-main)' }} />
                            </div>
                            <div>
                                <label>Earned Leave</label>
                                <input type="number" value={balance.earned} onChange={e => setBalance({ ...balance, earned: e.target.value })} style={{ width: '100%', padding: '0.5rem', border: '1px solid var(--input-border)', borderRadius: '0.5rem', background: 'var(--input-bg)', color: 'var(--text-main)' }} />
                            </div>
                        </div>
                    )
                )}

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1.5rem' }}>
                    <button onClick={onClose} className="btn btn-secondary">Close</button>
                    {selectedUser && <button onClick={handleSave} disabled={loading} className="btn btn-primary">Save Changes</button>}
                </div>
            </div>
        </div>
    );
};

const LeaveDashboard = () => {
    // ... hooks ...
    const navigate = useNavigate();
    const { ask, dialogs } = useDialogs();
    const [balance, setBalance] = useState(null);
    const [myLeaves, setMyLeaves] = useState([]);
    const [teamLeaves, setTeamLeaves] = useState([]);
    const [allLeaves, setAllLeaves] = useState([]);
    const [users, setUsers] = useState([]);
    const [holidays, setHolidays] = useState([]);
    const [showApplyModal, setShowApplyModal] = useState(false);
    const [showBalanceModal, setShowBalanceModal] = useState(false);
    const [allBalances, setAllBalances] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');

    // Edit Modal State
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedLeave, setSelectedLeave] = useState(null);
    const [selectedUser, setSelectedUser] = useState('');

    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const isManager = user.role === 'MANAGER' || user.role === 'ADMIN' || user.role === 'HR';
    const isHR = user.role === 'HR' || user.role === 'ADMIN';
    // The tab is in the address (?tab=team), so Home and emails can open Team Requests directly.
    const [activeTab, setActiveTab] = useTabParam(['my', ...(isManager ? ['team'] : []), ...(isHR ? ['all', 'absences', 'balances'] : []), 'calendar'], 'my');

    // ... useEffects & fetch functions remain same ...
    useEffect(() => {
        fetchData();
        if (isManager) fetchTeamData();
        if (isHR) {
            fetchAllLeaves();
            fetchUsersList();
            fetchAllBalances();
        }
    }, [isManager, isHR]);

    const fetchData = async () => {
        try {
            const [balRes, leavesRes, holidaysRes] = await Promise.all([
                api.get('/leaves/balance'),
                api.get('/leaves/my'),
                api.get(`/holidays?year=${new Date().getFullYear()}`)
            ]);
            setBalance(balRes.data);
            setMyLeaves(leavesRes.data);
            setHolidays(holidaysRes.data);
        } catch (error) {
            console.error('Error fetching data', error);
        }
    };

    const fetchTeamData = async () => {
        try {
            const res = await api.get('/leaves/team');
            setTeamLeaves(res.data);
        } catch (error) { console.error(error); }
    };

    const fetchAllLeaves = async () => {
        try {
            const res = await api.get('/leaves/all');
            setAllLeaves(res.data);
        } catch (error) { console.error(error); }
    };

    const fetchUsersList = async () => {
        try {
            const res = await api.get('/admin/users');
            setUsers(res.data);
        } catch (error) { console.error(error); }
    };

    const fetchAllBalances = async () => {
        try {
            const res = await api.get('/leaves/balances');
            setAllBalances(res.data);
        } catch (error) { console.error(error); }
    };

    // Actions
    const handleCancel = async (id) => {
        if (!window.confirm("Are you sure you want to cancel this request?")) return;
        try {
            await api.delete(`/leaves/${id}`);
            fetchData();
            alert("Leave cancelled.");
        } catch (e) { alert("Failed to cancel."); }
    };

    const handleManagerCancel = async (id) => {
        if (!window.confirm("Are you sure you want to cancel this APPROVED leave? This will refund the leave balance.")) return;
        try {
            await api.patch(`/leaves/${id}/cancel-approved`);
            fetchTeamData();
            if (isHR) fetchAllLeaves();
            alert("Approved leave cancelled and refunded.");
        } catch (e) { alert(e.response?.data?.error || "Failed to cancel."); }
    }

    const handleApproval = async (id, status, leave) => {
        let managerComment;
        if (status === 'REJECTED' && leave?.source === 'HR_MARKED') {
            // HR proposed these days as Leave Without Pay: they and the employee should know why it was not approved.
            managerComment = await ask({ title: 'Reject this Leave Without Pay?', message: 'HR and the employee will see your reason.', label: 'Reason', required: true, confirmText: 'Reject' });
            if (managerComment === null) return;
        }
        try {
            await api.patch(`/leaves/${id}/status`, { status, managerComment });
            fetchTeamData();
            if (isHR) fetchAllLeaves();
            alert(`Leave request ${status}`);
        } catch (error) {
            alert('Failed to update status');
        }
    };

    // ... getStatusBadge same ...
    const getStatusBadge = (status) => {
        const colors = {
            PENDING: '#f59e0b',
            APPROVED: '#10b981',
            REJECTED: '#ef4444',
            CANCELLED: '#6b7280'
        };
        return (
            <span style={{
                display: 'inline-flex', alignItems: 'center', gap: '4px',
                padding: '0.25rem 0.75rem', borderRadius: '999px', fontSize: '0.75rem', fontWeight: 'bold',
                backgroundColor: `${colors[status]}20`, color: colors[status]
            }}>
                {status}
            </span>
        );
    };


    const [showExportMenu, setShowExportMenu] = useState(false);
    const [showCustomDateModal, setShowCustomDateModal] = useState(false);

    const handleDownloadReport = async (rangeType, customStart, customEnd) => {
        let start, end;
        const now = new Date();

        if (rangeType === 'last-week') {
            start = new Date(now);
            start.setDate(now.getDate() - 7);
            end = now;
        } else if (rangeType === 'last-month') {
            start = new Date(now);
            start.setMonth(now.getMonth() - 1);
            end = now;
        } else if (rangeType === 'custom') {
            start = new Date(customStart);
            end = new Date(customEnd);
        }

        try {
            const response = await api.get('/leaves/report', {
                params: {
                    startDate: start.toISOString(),
                    endDate: end.toISOString()
                },
                responseType: 'blob'
            });

            // Trigger Download
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `leave_report_${start.toISOString().split('T')[0]}_${end.toISOString().split('T')[0]}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            setShowExportMenu(false);
        } catch (error) {
            console.error('Download failed', error);
            alert('Failed to download report');
        }
    };

    return (
        <div className="container section">
            {/* Custom Date Range Modal */}
            {showCustomDateModal && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 110 }}>
                    <div style={{ background: 'var(--surface)', padding: '1.5rem', borderRadius: '0.5rem', width: '300px' }}>
                        <h3 style={{ marginBottom: '1rem', fontWeight: 'bold' }}>Select Date Range</h3>
                        <form onSubmit={(e) => {
                            e.preventDefault();
                            const formData = new FormData(e.target);
                            handleDownloadReport('custom', formData.get('start'), formData.get('end'));
                            setShowCustomDateModal(false);
                        }}>
                            <div style={{ marginBottom: '0.5rem' }}>
                                <label style={{ display: 'block', fontSize: '0.8rem', marginBottom: '0.2rem' }}>Start Date</label>
                                <input name="start" type="date" required style={{ width: '100%', padding: '0.5rem', border: '1px solid var(--input-border)', borderRadius: '0.3rem', background: 'var(--input-bg)', color: 'var(--text-main)' }} />
                            </div>
                            <div style={{ marginBottom: '1rem' }}>
                                <label style={{ display: 'block', fontSize: '0.8rem', marginBottom: '0.2rem' }}>End Date</label>
                                <input name="end" type="date" required style={{ width: '100%', padding: '0.5rem', border: '1px solid var(--input-border)', borderRadius: '0.3rem', background: 'var(--input-bg)', color: 'var(--text-main)' }} />
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                                <button type="button" onClick={() => setShowCustomDateModal(false)} className="btn btn-secondary">Cancel</button>
                                <button type="submit" className="btn btn-primary">Download</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Header ... */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
                <button onClick={() => navigate('/dashboard')} style={{ display: 'flex', alignItems: 'center', color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer' }}>
                    <ArrowLeft size={20} style={{ marginRight: '0.5rem' }} /> Back to Home
                </button>

                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                    {/* Export Dropdown */}
                    <div style={{ position: 'relative' }}>
                        <button
                            onClick={() => setShowExportMenu(!showExportMenu)}
                            className="btn btn-secondary"
                            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                        >
                            <Download size={18} /> Export <ChevronDown size={14} />
                        </button>
                        {showExportMenu && (
                            <div style={{
                                position: 'absolute', top: '100%', right: 0, marginTop: '0.5rem',
                                background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '0.5rem',
                                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', minWidth: '160px', zIndex: 50
                            }}>
                                <button
                                    onClick={() => handleDownloadReport('last-week')}
                                    style={{ display: 'block', width: '100%', padding: '0.75rem 1rem', textAlign: 'left', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '0.9rem' }}
                                    onMouseOver={(e) => e.target.style.background = '#f3f4f6'}
                                    onMouseOut={(e) => e.target.style.background = 'transparent'}
                                >
                                    Last Week
                                </button>
                                <button
                                    onClick={() => handleDownloadReport('last-month')}
                                    style={{ display: 'block', width: '100%', padding: '0.75rem 1rem', textAlign: 'left', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '0.9rem' }}
                                    onMouseOver={(e) => e.target.style.background = '#f3f4f6'}
                                    onMouseOut={(e) => e.target.style.background = 'transparent'}
                                >
                                    Last Month
                                </button>
                                <button
                                    onClick={() => { setShowCustomDateModal(true); setShowExportMenu(false); }}
                                    style={{ display: 'block', width: '100%', padding: '0.75rem 1rem', textAlign: 'left', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '0.9rem' }}
                                    onMouseOver={(e) => e.target.style.background = '#f3f4f6'}
                                    onMouseOut={(e) => e.target.style.background = 'transparent'}
                                >
                                    Custom Range...
                                </button>
                            </div>
                        )}
                    </div>
                    {/* This closing parenthesis was likely a typo and is removed */}

                    {isHR && (
                        <button onClick={() => setShowBalanceModal(true)} className="btn btn-secondary">
                            <Settings size={18} style={{ marginRight: '0.5rem' }} /> Manage Balances
                        </button>
                    )}
                    <button onClick={() => setShowApplyModal(true)} className="btn btn-primary">
                        <Plus size={20} style={{ marginRight: '0.5rem' }} /> Apply Leave
                    </button>
                </div>
            </div >

            {/* Tab Navigation */}
            < div style={{ display: 'flex', background: 'var(--bg-secondary)', padding: '4px', borderRadius: '0.5rem', width: 'fit-content', marginBottom: '2rem' }}>
                <button onClick={() => setActiveTab('my')} style={{ padding: '0.5rem 1rem', borderRadius: '0.3rem', border: 'none', cursor: 'pointer', background: activeTab === 'my' ? 'white' : 'transparent', color: activeTab === 'my' ? 'var(--primary)' : 'var(--text-muted)', fontWeight: activeTab === 'my' ? '600' : '500' }}>My Leaves</button>
                {isManager && <button onClick={() => setActiveTab('team')} style={{ padding: '0.5rem 1rem', borderRadius: '0.3rem', border: 'none', cursor: 'pointer', background: activeTab === 'team' ? 'white' : 'transparent', color: activeTab === 'team' ? 'var(--primary)' : 'var(--text-muted)', fontWeight: activeTab === 'team' ? '600' : '500' }}>Team Requests</button>}
                {isHR && <button onClick={() => setActiveTab('all')} style={{ padding: '0.5rem 1rem', borderRadius: '0.3rem', border: 'none', cursor: 'pointer', background: activeTab === 'all' ? 'white' : 'transparent', color: activeTab === 'all' ? 'var(--primary)' : 'var(--text-muted)', fontWeight: activeTab === 'all' ? '600' : '500' }}>All Company Leaves</button>}
                {isHR && <button onClick={() => setActiveTab('absences')} style={{ padding: '0.5rem 1rem', borderRadius: '0.3rem', border: 'none', cursor: 'pointer', background: activeTab === 'absences' ? 'white' : 'transparent', color: activeTab === 'absences' ? 'var(--primary)' : 'var(--text-muted)', fontWeight: activeTab === 'absences' ? '600' : '500' }}>Absences</button>}
                {isHR && <button onClick={() => setActiveTab('balances')} style={{ padding: '0.5rem 1rem', borderRadius: '0.3rem', border: 'none', cursor: 'pointer', background: activeTab === 'balances' ? 'white' : 'transparent', color: activeTab === 'balances' ? 'var(--primary)' : 'var(--text-muted)', fontWeight: activeTab === 'balances' ? '600' : '500' }}>Employee Balances</button>}
                <button onClick={() => setActiveTab('calendar')} style={{ padding: '0.5rem 1rem', borderRadius: '0.3rem', border: 'none', cursor: 'pointer', background: activeTab === 'calendar' ? 'white' : 'transparent', color: activeTab === 'calendar' ? 'var(--primary)' : 'var(--text-muted)', fontWeight: activeTab === 'calendar' ? '600' : '500' }}>Holiday Calendar</button>
            </div >

            {activeTab === 'my' && (
                <>
                    {/* Stats ... */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>
                        {[
                            { label: 'Casual Leave', value: balance?.casual || 0, color: '#3b82f6' },
                            { label: 'Sick Leave', value: balance?.sick || 0, color: '#ef4444' },
                            { label: 'Earned Leave', value: balance?.earned || 0, color: '#10b981' },
                        ].map((item, index) => (
                            <div key={index} className="glass-card" style={{ padding: '1.5rem' }}>
                                <div style={{ color: 'var(--text-muted)', fontSize: '0.875rem', marginBottom: '0.5rem' }}>{item.label}</div>
                                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: item.color }}>{item.value}</div>
                                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Days Available</div>
                            </div>
                        ))}
                    </div>

                    <div className="glass-card" style={{ padding: 0, overflow: 'hidden' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead style={{ background: 'var(--bg-secondary)', borderBottom: '1px solid #e5e7eb' }}>
                                <tr>
                                    <th style={{ padding: '1rem', textAlign: 'left' }}>Type</th>
                                    <th style={{ padding: '1rem', textAlign: 'left' }}>Dates</th>
                                    <th style={{ padding: '1rem', textAlign: 'left' }}>Reason</th>
                                    <th style={{ padding: '1rem', textAlign: 'left' }}>Status</th>
                                    <th style={{ padding: '1rem', textAlign: 'right' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {myLeaves.map(leave => (
                                    <tr key={leave.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                                        <td style={{ padding: '1rem', fontWeight: '500' }}>{leaveTypeLabel(leave.type)}</td>
                                        <td style={{ padding: '1rem', color: 'var(--text-secondary)' }}>
                                            {new Date(leave.startDate).toLocaleDateString()} - {new Date(leave.endDate).toLocaleDateString()}
                                            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>({formatLeaveDays(leave)})</div>
                                        </td>
                                        <td style={{ padding: '1rem', color: 'var(--text-muted)' }}>
                                            {leave.reason}
                                            {leave.source === 'HR_MARKED' && <div style={{ fontSize: '0.75rem', color: '#b45309' }}>Proposed by HR{leave.markedBy?.name ? `: ${leave.markedBy.name}` : ''} for an absence</div>}
                                            {leave.managerComment && <div style={{ fontSize: '0.75rem' }}>Manager: {leave.managerComment}</div>}
                                        </td>
                                        <td style={{ padding: '1rem' }}>{getStatusBadge(leave.status)}</td>
                                        <td style={{ padding: '1rem', textAlign: 'right', display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                                            {leave.status === 'PENDING' && (
                                                <>
                                                    <button onClick={() => { setSelectedLeave(leave); setShowEditModal(true); }} style={{ color: '#3b82f6', border: 'none', background: 'none', cursor: 'pointer' }} title="Edit Request">
                                                        <Edit size={18} />
                                                    </button>
                                                    <button onClick={() => handleCancel(leave.id)} style={{ color: '#ef4444', border: 'none', background: 'none', cursor: 'pointer' }} title="Cancel Request">
                                                        <Trash2 size={18} />
                                                    </button>
                                                </>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}

            {
                (activeTab === 'team' || activeTab === 'all') && (
                    <div className="glass-card" style={{ padding: 0, overflow: 'hidden' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead style={{ background: 'var(--bg-secondary)', borderBottom: '1px solid #e5e7eb' }}>
                                <tr>
                                    <th style={{ padding: '1rem', textAlign: 'left' }}>Employee</th>
                                    <th style={{ padding: '1rem', textAlign: 'left' }}>Type</th>
                                    <th style={{ padding: '1rem', textAlign: 'left' }}>Dates</th>
                                    <th style={{ padding: '1rem', textAlign: 'left' }}>Reason</th>
                                    <th style={{ padding: '1rem', textAlign: 'left' }}>Status</th>
                                    <th style={{ padding: '1rem', textAlign: 'right' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(activeTab === 'team' ? teamLeaves : allLeaves).map(leave => (
                                    <tr key={leave.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                                        <td style={{ padding: '1rem' }}>
                                            <div style={{ fontWeight: '500' }}>{leave.user?.name}</div>
                                            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{leave.user?.email}</div>
                                        </td>
                                        <td style={{ padding: '1rem' }}>{leaveTypeLabel(leave.type)}</td>
                                        <td style={{ padding: '1rem', color: 'var(--text-secondary)' }}>
                                            {new Date(leave.startDate).toLocaleDateString()} - {new Date(leave.endDate).toLocaleDateString()}
                                            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>({formatLeaveDays(leave)})</div>
                                        </td>
                                        <td style={{ padding: '1rem', color: 'var(--text-muted)' }}>
                                            {leave.reason}
                                            {leave.source === 'HR_MARKED' && <div style={{ fontSize: '0.75rem', color: '#b45309' }}>Proposed by HR{leave.markedBy?.name ? `: ${leave.markedBy.name}` : ''} for an absence</div>}
                                            {leave.managerComment && <div style={{ fontSize: '0.75rem' }}>Manager: {leave.managerComment}</div>}
                                        </td>
                                        <td style={{ padding: '1rem' }}>{getStatusBadge(leave.status)}</td>
                                        <td style={{ padding: '1rem', textAlign: 'right' }}>
                                            {leave.status === 'PENDING' && (
                                                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                                                    <button onClick={() => handleApproval(leave.id, 'APPROVED')} style={{ padding: '0.4rem 0.8rem', background: '#10b981', color: 'white', border: 'none', borderRadius: '0.3rem', cursor: 'pointer', fontSize: '0.8rem' }}>Approve</button>
                                                    <button onClick={() => handleApproval(leave.id, 'REJECTED', leave)} style={{ padding: '0.4rem 0.8rem', background: '#ef4444', color: 'white', border: 'none', borderRadius: '0.3rem', cursor: 'pointer', fontSize: '0.8rem' }}>Reject</button>
                                                </div>
                                            )}
                                            {leave.status === 'APPROVED' && (
                                                <button onClick={() => handleManagerCancel(leave.id)} style={{ padding: '0.4rem 0.8rem', background: '#fee2e2', color: '#ef4444', border: 'none', borderRadius: '0.3rem', cursor: 'pointer', fontSize: '0.8rem' }}>Cancel</button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )
            }

            {activeTab === 'absences' && isHR && (
                <AbsencesTab onProposed={() => { if (isManager) fetchTeamData(); fetchAllLeaves(); }} />
            )}

            {activeTab === 'balances' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                    <div className="glass-card" style={{ padding: '1rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <div style={{ position: 'relative', flex: 1 }}>
                            <input
                                type="text"
                                placeholder="Search employee or department..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                style={{ width: '100%', padding: '0.75rem 1rem', paddingLeft: '2.5rem', borderRadius: '0.5rem', border: '1px solid var(--border)', outline: 'none' }}
                            />
                            <Clock size={18} style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                        </div>
                    </div>

                    <div className="glass-card" style={{ padding: 0, overflow: 'hidden' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead style={{ background: 'var(--bg-secondary)', borderBottom: '1px solid #e5e7eb' }}>
                                <tr>
                                    <th style={{ padding: '1rem', textAlign: 'left' }}>Employee</th>
                                    <th style={{ padding: '1rem', textAlign: 'left' }}>Department</th>
                                    <th style={{ padding: '1rem', textAlign: 'center' }}>Casual</th>
                                    <th style={{ padding: '1rem', textAlign: 'center' }}>Sick</th>
                                    <th style={{ padding: '1rem', textAlign: 'center' }}>Earned</th>
                                    <th style={{ padding: '1rem', textAlign: 'right' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {allBalances
                                    .filter(item =>
                                        item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                                        item.department?.toLowerCase().includes(searchQuery.toLowerCase())
                                    )
                                    .map(item => (
                                        <tr key={item.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                                            <td style={{ padding: '1rem' }}>
                                                <div style={{ fontWeight: '500' }}>{item.name}</div>
                                                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{item.email}</div>
                                            </td>
                                            <td style={{ padding: '1rem' }}>{item.department || 'N/A'}</td>
                                            <td style={{ padding: '1rem', textAlign: 'center' }}>{item.leaveBalance?.casual || 0}</td>
                                            <td style={{ padding: '1rem', textAlign: 'center' }}>{item.leaveBalance?.sick || 0}</td>
                                            <td style={{ padding: '1rem', textAlign: 'center' }}>{item.leaveBalance?.earned || 0}</td>
                                            <td style={{ padding: '1rem', textAlign: 'right' }}>
                                                <button
                                                    onClick={() => { setSelectedUser(item.id); setShowBalanceModal(true); }}
                                                    className="btn btn-secondary"
                                                    style={{ padding: '0.4rem 0.8rem', fontSize: '0.8rem' }}
                                                >
                                                    Adjust
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {
                activeTab === 'calendar' && (
                    <HolidayCalendar userId={user.id} showTeamLeaves={activeTab === 'team' || isManager} />
                )
            }

            <ApplyLeaveModal
                show={showApplyModal}
                onClose={() => setShowApplyModal(false)}
                onSuccess={() => { fetchData(); if (isManager) fetchTeamData(); if (isHR) fetchAllLeaves(); }}
                balance={balance}
                holidays={holidays}
            />

            <EditLeaveModal
                show={showEditModal}
                leave={selectedLeave}
                onClose={() => setShowEditModal(false)}
                onSuccess={() => { fetchData(); }}
                holidays={holidays}
            />

            {dialogs}

            <AdjustBalanceModal
                show={showBalanceModal}
                onClose={() => setShowBalanceModal(false)}
                onSuccess={() => { fetchData(); fetchAllBalances(); }}
                users={users}
                selectedUser={selectedUser}
                setSelectedUser={setSelectedUser}
            />
        </div >
    );
};

export default LeaveDashboard;
